// src/components/shell/PageNav.js
// Sous-menu de page (« deuxième sidebar ») sur mobile.
//
// Certaines pages ont leur propre nav en colonne à côté du rail de l'app —
// illisible sur 360px. Ici la page déclare ses entrées, le shell affiche un
// bouton tout à droite de la barre du haut, et les entrées s'ouvrent dans un
// drawer qui coulisse de la droite. Une seule implémentation pour toutes les
// pages concernées (execution, strategies, agent…).
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

const PageNavContext = createContext(null);

/**
 * Libellé d'une entrée, quelle que soit la forme employée par la page.
 *
 * `label` : chaîne déjà résolue (JournalAutoPage).
 * `labelKey` : clé i18n (ExecutionPage) — une constante de module ne peut pas
 * appeler `t()`, et `usePageNav` réclame une référence stable, donc la
 * traduction se fait forcément ici, au rendu.
 */
function itemLabel(item, t) {
  if (!item) return "";
  if (item.label) return item.label;
  if (item.labelKey) return t(item.labelKey);
  return "";
}

export function PageNavProvider({ children }) {
  const [nav, setNav] = useState(null);
  const [open, setOpen] = useState(false);
  // Corps du drawer : une page dont le sous-menu n'est pas une simple liste
  // d'entrées (recherche, tri, cartes à actions…) y projette son propre panneau.
  const [bodyEl, setBodyEl] = useState(null);
  return (
    <PageNavContext.Provider value={{ nav, setNav, open, setOpen, bodyEl, setBodyEl }}>
      {children}
    </PageNavContext.Provider>
  );
}

// Appelé par une page pour publier son sous-menu.
// `items` doit être une référence stable (constante de module), sinon on
// réenregistrerait à chaque rendu.
export function usePageNav({ items, activeKey, onSelect, title, buttonLabel, buttonIcon }) {
  const ctx = useContext(PageNavContext);
  const set = ctx?.setNav;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!set) return undefined;
    set({ items, activeKey, title, buttonLabel, buttonIcon, onSelect: (k) => onSelectRef.current?.(k) });
    return () => set(null);
  }, [set, items, activeKey, title, buttonLabel, buttonIcon]);
}

export function usePageNavShell() {
  return useContext(PageNavContext) || {};
}

// Bouton de la barre du haut : rien à afficher si la page n'a pas de sous-menu.
export function PageNavButton() {
  const { nav, setOpen } = usePageNavShell();
  const { t } = useTranslation();
  if (!nav) return null;

  const active = nav.items?.find((i) => i.key === nav.activeKey);
  const Icon = active?.icon || nav.buttonIcon;
  const activeLabel = itemLabel(active, t);

  return (
    <button
      onClick={() => setOpen(true)}
      aria-label={nav.title || "Options de la page"}
      className="fm-tap flex h-11 shrink-0 items-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-2.5 text-violet-700 dark:border-violet-400/30 dark:bg-violet-500/10 dark:text-violet-200"
    >
      {Icon ? <Icon size={16} className="shrink-0" /> : null}
      <span className="max-w-[92px] truncate text-[13px] font-semibold">
        {activeLabel || nav.buttonLabel || nav.title}
      </span>
    </button>
  );
}

// Drawer coulissant depuis la droite, rendu par le shell.
export function PageNavDrawer() {
  const { nav, open, setOpen, setBodyEl } = usePageNavShell();
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  // La page peut se démonter alors que le drawer est ouvert.
  useEffect(() => {
    if (!nav) setOpen?.(false);
  }, [nav, setOpen]);

  if (!open || !nav) return null;

  return (
    <div className="fixed inset-0 z-[65] lg:hidden" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />

      <aside className="fm-pagenav absolute inset-y-0 right-0 flex w-[min(300px,86vw)] flex-col border-l border-slate-200 bg-white pt-[env(safe-area-inset-top)] dark:border-white/10 dark:bg-[#181a2e]">
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-slate-100 px-3 dark:border-white/5">
          <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-slate-900 dark:text-white">
            {nav.title || "Options"}
          </span>
          <button
            onClick={() => setOpen(false)}
            aria-label="Fermer"
            className="fm-tap grid h-11 w-11 shrink-0 place-items-center rounded-xl text-slate-500 dark:text-slate-300"
          >
            <X size={20} />
          </button>
        </div>

        {/* Pas d'entrées : la page projette son propre panneau ici (voir bodyEl). */}
        {!nav.items?.length ? (
          <div
            ref={setBodyEl}
            className="min-h-0 flex-1 overflow-y-auto pb-[max(0.5rem,env(safe-area-inset-bottom))]"
          />
        ) : (
        <div
          className="min-h-0 flex-1 overflow-y-auto p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
          role="tablist"
        >
          {nav.items.map((item) => {
            const Icon = item.icon;
            const active = item.key === nav.activeKey;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  nav.onSelect?.(item.key);
                  setOpen(false);
                }}
                className={[
                  "fm-tap flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-[15px] font-medium",
                  active
                    ? "bg-violet-600 text-white"
                    : "text-slate-700 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-white/5",
                ].join(" ")}
              >
                {Icon ? (
                  <Icon size={18} className={active ? "shrink-0 text-white" : "shrink-0 text-slate-400 dark:text-slate-400"} />
                ) : null}
                <span className="min-w-0 truncate">{itemLabel(item, t)}</span>
              </button>
            );
          })}
        </div>
        )}
      </aside>
    </div>
  );
}
